import { MouseEvent, TouchEvent } from 'react';

type ReleaseEventType = 'mouseup' | 'touchend';

export type PressEvent<T> = TouchEvent<T> | MouseEvent<T>;

export function isTouchEvent<T>(event: PressEvent<T>): event is TouchEvent<T> {
  return 'changedTouches' in event;
}

export interface ButtonHandlers<T = {}> {
  onPress?(e: PressEvent<T>): void;
  onRelease?(e: PressEvent<T>): void;
  onGlobalRelease?(): void;
  onClick?(e: PressEvent<T>): void;
}

export interface ButtonBoilerplate<T = {}> {
  readonly _globalEvents: {
    touchend?: { handler(): void; listening: boolean };
    mouseup?: { handler(): void; listening: boolean };
  };
  readonly _state: {
    downFromThisButton: boolean;
    pressX: number;
    pressY: number;
  };
  props: {
    onTouchStart?(e: TouchEvent<T>): void;
    onTouchEnd?(e: TouchEvent<T>): void;
    onMouseDown?(e: MouseEvent<T>): void;
    onMouseUp?(e: MouseEvent<T>): void;
    onClick?(e: MouseEvent<T>): void;
  };
  tearDown(): void;
}

const isTouchDevice = 'ontouchstart' in document;
function warn() {
  window.removeEventListener('touchstart', warn);
  if (!isTouchDevice)
    console.warn(
            [
              '이미 스토어앱이 실행중인 상태에서 개발자도구를 열어 모바일 에뮬레이션을 켰을 경우,',
              '<Tappable> 버튼들이 정상적으로 동작하지 않을 수 있습니다. 브라우저 창을 새로고침 해주세요.',
            ].join('\n'),
        );
}
// https://www.chromestatus.com/feature/5093566007214080
// https://developer.mozilla.org/en-US/docs/Web/API/EventTarget/addEventListener
window.addEventListener('touchstart', warn, { passive: false } as any);

export default function buttonBoilerplate<H extends ButtonHandlers<T>, T = {}>(
    { onPress, onRelease, onGlobalRelease, onClick }: H,
    previousBoilerplate?: ButtonBoilerplate<T>,
    thresholdOfCancelClickWithPanning: number = 100,
): ButtonBoilerplate<T> {
  if (previousBoilerplate) {
    previousBoilerplate.tearDown();
  }
  const boilerplate = previousBoilerplate || {
    _globalEvents: {},
    _state: {
      downFromThisButton: false,
      pressX: 0,
      pressY: 0,
    },
    props: {},
    tearDown() {
      if (isTouchDevice) removeGlobalListener('touchend', true);
      else removeGlobalListener('mouseup', true);
    },
  };
  if (isTouchDevice) {
    boilerplate._globalEvents.touchend = boilerplate._globalEvents.touchend || {
      handler() {
        if (boilerplate._state.downFromThisButton) onGlobalRelease && onGlobalRelease();
        boilerplate._state.downFromThisButton = false;
      },
      listening: false,
    };
    addGlobalListener('touchend');
    boilerplate.props = {
      onTouchStart(e: TouchEvent<T>) {
        const { pageX, pageY } = e.changedTouches[0];
        Object.assign(boilerplate._state as any, {
          downFromThisButton: true,
          pressX: pageX,
          pressY: pageY,
        });
        onPress && onPress(e);
      },
      onTouchEnd(e: TouchEvent<T>) {
        const { downFromThisButton } = boilerplate._state;
        const { pageX, pageY } = e.changedTouches[0];
        try {
          onRelease && onRelease(e);
        } finally {
          if (downFromThisButton && checkThreshold(pageX, pageY)) {
            onClick && onClick(e);
          }
        }
      },
    };
  } else {
    if (boilerplate._globalEvents.mouseup && boilerplate._globalEvents.mouseup.listening)
      addGlobalListener('mouseup');
    boilerplate._globalEvents.mouseup = boilerplate._globalEvents.mouseup || {
      handler() {
        removeGlobalListener('mouseup');
        onGlobalRelease && onGlobalRelease();
      },
      listening: false,
    };
    boilerplate.props = {
      onMouseDown(e: MouseEvent<T>) {
        const { pageX, pageY } = e;
        Object.assign(boilerplate._state as any, {
          pressX: pageX,
          pressY: pageY,
        });
        addGlobalListener('mouseup');
        onPress && onPress(e);
      },
      onMouseUp: onRelease && ((e: MouseEvent<T>) => onRelease(e)),
      onClick:
                onClick &&
                ((e: MouseEvent<T>) => {
                  if (onClick && checkThreshold(e.pageX, e.pageY)) onClick(e);
                }),
    };
  }
  function addGlobalListener(type: ReleaseEventType) {
    const event = boilerplate._globalEvents[type];
    if (event && event.handler) {
      window.addEventListener(type, event.handler);
      event.listening = true;
    }
  }
  function removeGlobalListener(type: ReleaseEventType, tearDown: boolean = false) {
    const event = boilerplate._globalEvents[type];
    if (event && event.handler) {
      window.removeEventListener(type, event.handler);
      if (!tearDown) {
        event.listening = false;
      }
    }
  }
  function checkThreshold(x: number, y: number) {
    const { pressX, pressY } = boilerplate._state;
    const [dx, dy] = [pressX - x, pressY - y];
    const powerOfThreshold =
            thresholdOfCancelClickWithPanning * thresholdOfCancelClickWithPanning;
    return powerOfThreshold >= dx * dx + dy * dy;
  }
  return boilerplate;
}
